import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Stethoscope, Users, Award } from 'lucide-react';

const AboutUsModal = ({ isOpen, onClose, initialData }) => {
    const [aboutData, setAboutData] = useState({
        title: "",
        description: "",
        features: Array(4).fill({ text: "" }),
        imageUrl: "",
        buttonText: ""
    });

    const [activeTab, setActiveTab] = useState('main');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchCurrentData = async () => {
            if (isOpen && !initialData) {
                try {
                    setIsLoading(true);
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/home/about-us/`);
                    if (response.ok) {
                        const data = await response.json();
                        setAboutData({
                            title: data.title || "",
                            description: data.description || "",
                            features: data.features || Array(4).fill({ text: "" }),
                            imageUrl: data.imageUrl || "",
                            buttonText: data.buttonText || "",
                            buttonLink:data.buttonLink || "",

                        });
                    }
                } catch (error) {
                    console.error('Error fetching about us data:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchCurrentData();
    }, [isOpen, initialData]);

    useEffect(() => {
        if (initialData) {
            setAboutData(initialData);
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAboutData(prev => ({ ...prev, [name]: value }));
    };

    const handleFeatureChange = (index, value) => {
        const updatedFeatures = [...aboutData.features];
        updatedFeatures[index] = { text: value };
        setAboutData(prev => ({ ...prev, features: updatedFeatures }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('category', 'about_images');

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            setAboutData(prev => ({
                ...prev,
                imageUrl: response.data.image || response.data.image.url
            }));
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleSubmit = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/home/about-us/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(aboutData)
            });

            if (response.ok) {
                const updatedData = await response.json();
                onClose();
            } else {
                console.error('Failed to update about us data');
                alert('Failed to save changes. Please try again.');
            }
        } catch (error) {
            console.error('Error updating about us data:', error);
            alert('An error occurred while saving. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
                <div className="modal-box">
                    <span className="loading loading-spinner loading-lg"></span>
                    <p>Loading about us data...</p>
                </div>
            </dialog>
        );
    }

    const featureIcons = [<Building2 className="w-5 h-5" />, <Stethoscope className="w-5 h-5" />, <Users className="w-5 h-5" />, <Award className="w-5 h-5" />];

    return (
        <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
            <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] relative">
                <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
                <h3 className="font-bold text-lg mb-4">Edit About Us Section</h3>
                
                <div className="tabs tabs-boxed mb-6">
                    <button 
                        className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('main')}
                    >
                        Main Content
                    </button>
                    <button 
                        className={`tab ${activeTab === 'features' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('features')}
                    >
                        Features
                    </button>
                </div>
                
                <div className="overflow-y-auto max-h-[60vh] pr-2">
                    {activeTab === 'main' && (
                        <div className="space-y-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Title</span>
                                </label>
                                <input 
                                    type="text" 
                                    name="title" 
                                    value={aboutData.title || ''} 
                                    onChange={handleChange} 
                                    className="input input-bordered w-full" 
                                />
                            </div>
                            
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Description</span>
                                </label>
                                <textarea 
                                    name="description" 
                                    value={aboutData.description || ''} 
                                    onChange={handleChange} 
                                    className="textarea textarea-bordered w-full h-32" 
                                    placeholder="First line will be shown as the main paragraph, subsequent lines as secondary text"
                                />
                            </div>
                            
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Button Text</span>
                                </label>
                                <input 
                                    type="text" 
                                    name="buttonText" 
                                    value={aboutData.buttonText || ''} 
                                    onChange={handleChange} 
                                    className="input input-bordered w-full" 
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Button Link</span>
                                </label>
                                <input 
                                    type="text" 
                                    name="buttonLink" 
                                    value={aboutData.buttonLink || ''} 
                                    onChange={handleChange} 
                                    className="input input-bordered w-full" 
                                />
                            </div>
                            
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Image</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="file-input file-input-bordered w-full max-w-xs"
                                        disabled={isUploading}
                                    />
                                    {isUploading && (
                                        <progress 
                                            className="progress progress-primary w-56" 
                                            value={uploadProgress} 
                                            max="100"
                                        ></progress>
                                    )}
                                </div>
                                {aboutData.imageUrl && (
                                    <div className="mt-2">
                                        <div className="text-sm text-gray-500 mb-1">Current Image:</div>
                                        <img 
                                            src={aboutData.imageUrl} 
                                            alt="About us preview" 
                                            className="max-h-40 rounded-md border"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'features' && (
                        <div className="space-y-4">
                            {aboutData.features?.map((feature, index) => (
                                <div key={index} className="card bg-base-100 shadow-sm p-4">
                                    <h4 className="font-bold mb-3 flex items-center gap-2">
                                        {featureIcons[index] || featureIcons[0]}
                                        Feature {index + 1}
                                    </h4>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Feature Text</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={feature?.text || ''} 
                                            onChange={(e) => handleFeatureChange(index, e.target.value)} 
                                            className="input input-bordered w-full" 
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="modal-action mt-4">
                    <button className="btn" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSubmit}>
                        Save Changes
                    </button>
                </div>
            </div>
        </dialog>
    );
};

export default AboutUsModal;